import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { parametersAPI } from '../../services/api';
import { sanitizeFieldSchema } from '../../utils/sanitizeSchema';

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchFieldSchemas = createAsyncThunk(
  'fieldsManager/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await parametersAPI.fieldSchemas.list(params);
      // Coerce any legacy multilang {en,tr} name/description to strings so the
      // list can render them (React error #31 otherwise).
      const items = (res.data.items || res.data.fieldSchemas || []).map(sanitizeFieldSchema);
      return { ...res.data, items };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch field schemas');
    }
  }
);

export const fetchFieldSchema = createAsyncThunk(
  'fieldsManager/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const res = await parametersAPI.fieldSchemas.get(id);
      // Deliberately NOT localized: this feeds the Form Builder, which edits
      // the raw multilang values. Consumers that only display a schema must
      // run it through localizeFieldSchema themselves.
      const item = res.data.item || res.data.fieldSchema || res.data;
      return { ...res.data, item };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch field schema');
    }
  }
);

export const createFieldSchema = createAsyncThunk(
  'fieldsManager/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await parametersAPI.fieldSchemas.create(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to create field schema');
    }
  }
);

export const updateFieldSchema = createAsyncThunk(
  'fieldsManager/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await parametersAPI.fieldSchemas.update(id, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to update field schema');
    }
  }
);

export const duplicateFieldSchema = createAsyncThunk(
  'fieldsManager/duplicate',
  async (id, { rejectWithValue }) => {
    try {
      const res = await parametersAPI.fieldSchemas.duplicate(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to duplicate field schema');
    }
  }
);

export const deactivateFieldSchema = createAsyncThunk(
  'fieldsManager/deactivate',
  async (id, { rejectWithValue }) => {
    try {
      const res = await parametersAPI.fieldSchemas.deactivate(id);
      return { id, item: res.data.item };
    } catch (err) {
      // Keep the whole payload: a 403 carries { code: 'STANDARD_TEMPLATE' }
      // which the UI needs for a localized message.
      return rejectWithValue(err.response?.data || { error: 'Failed to deactivate field schema' });
    }
  }
);

export const deleteFieldSchema = createAsyncThunk(
  'fieldsManager/delete',
  async (id, { rejectWithValue }) => {
    try {
      await parametersAPI.fieldSchemas.delete(id);
      return { id };
    } catch (err) {
      // Keep the whole payload: a 409 carries { code: 'FIELD_SCHEMA_IN_USE',
      // contractCount, ... } which the UI needs for a localized message.
      return rejectWithValue(err.response?.data || { error: 'Failed to delete field schema' });
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────────

const fieldsManagerSlice = createSlice({
  name: 'fieldsManager',
  initialState: {
    items: [],           // list of field schema summaries
    currentSchema: null, // full schema with sections (loaded on demand)
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearCurrentSchema: (state) => {
      state.currentSchema = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFieldSchemas
      .addCase(fetchFieldSchemas.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFieldSchemas.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
      })
      .addCase(fetchFieldSchemas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchFieldSchema (single)
      .addCase(fetchFieldSchema.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFieldSchema.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSchema = action.payload.item;
      })
      .addCase(fetchFieldSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // createFieldSchema
      .addCase(createFieldSchema.pending, (state) => { state.saving = true; state.error = null; })
      .addCase(createFieldSchema.fulfilled, (state, action) => {
        state.saving = false;
        state.currentSchema = action.payload.item;
        // Add to list (summary)
        const { sections, ...summary } = action.payload.item;
        state.items.unshift(summary);
      })
      .addCase(createFieldSchema.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // updateFieldSchema
      .addCase(updateFieldSchema.pending, (state) => { state.saving = true; state.error = null; })
      .addCase(updateFieldSchema.fulfilled, (state, action) => {
        state.saving = false;
        state.currentSchema = action.payload.item;
        // Update in list
        const idx = state.items.findIndex(i => i._id === action.payload.item._id);
        if (idx >= 0) {
          const { sections, ...summary } = action.payload.item;
          state.items[idx] = summary;
        }
      })
      .addCase(updateFieldSchema.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // duplicateFieldSchema
      .addCase(duplicateFieldSchema.fulfilled, (state, action) => {
        const { sections, ...summary } = action.payload.item;
        state.items.unshift(summary);
      })

      // deactivateFieldSchema
      .addCase(deactivateFieldSchema.fulfilled, (state, action) => {
        const idx = state.items.findIndex(i => i._id === action.payload.id);
        if (idx >= 0) {
          state.items[idx] = { ...state.items[idx], isActive: false };
        }
      })

      // deleteFieldSchema
      .addCase(deleteFieldSchema.fulfilled, (state, action) => {
        state.items = state.items.filter(i => i._id !== action.payload.id);
      });
  },
});

export const { clearCurrentSchema, clearError } = fieldsManagerSlice.actions;
export default fieldsManagerSlice.reducer;
